import "./AppointmentCalendar.css";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { fetchAgoraToken } from "../components/Agora/helpers";
import { openVideoCallTab } from "../utils/videoSessionDevices";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import AddAppointment from "../outlets/AddAppointments";
import CheckInDetailsDrawer from "./CheckInDetailsDrawer";
import {
  getCmStatusColors,
  getCmStatusSlug,
  mapAppointmentToCmDisplayStatus,
} from "../utils/cmAppointmentStatus";
import axios from "axios";
import dayGridPlugin from "@fullcalendar/daygrid";
import EditAppointment from "../outlets/EditAppointment";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import logo from "../assets/Numente.png";
import moment from "moment-timezone";
import RescheduleAppointment from "../outlets/RescheduleAppointment";
import timeGridPlugin from "@fullcalendar/timegrid";

const AppointmentCalendar = () => {
  const [agoraCredentials, setAgoraCredentials] = useState(null);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelAppointmentScope, setCancelAppointmentScope] =
    useState("single");
  const [cancellationReason, setCancellationReason] = useState("");
  const [headerTitle, setHeaderTitle] = useState("");
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [customMessage, setCustomMessage] = useState("");
  const [events, setEvents] = useState([]);
  const [hideNoSessionMessage, setHideNoSessionMessage] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [isCancellingAppointment, setIsCancellingAppointment] = useState(false);
  const [isLoadingAgoraToken, setIsLoadingAgoraToken] = useState(false);
  const [multipleAppointments, setMultipleAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [detailsAppointment, setDetailsAppointment] = useState(null);
  const [detailsDisplayStatus, setDetailsDisplayStatus] = useState("");
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [showCheckInDetailsDrawer, setShowCheckInDetailsDrawer] =
    useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showEditAppointment, setShowEditAppointment] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showMultipleAppointments, setShowMultipleAppointments] =
    useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [visibleDateRange, setVisibleDateRange] = useState({
    end: null,
    start: null,
  });

  const calendarRef = useRef(null);
  const tooltipHideTimeoutRef = useRef(null);

  const buildHoveredEventPayload = (appointment, status) => ({
    ...appointment,
    extendedProps: {
      canRescheduleOrCancel:
        status !== "Completed" && status !== "Cancelled",
      newStatus: status,
    },
  });

  const clearTooltipHideTimeout = () => {
    if (tooltipHideTimeoutRef.current) {
      clearTimeout(tooltipHideTimeoutRef.current);
      tooltipHideTimeoutRef.current = null;
    }
  };

  const showAppointmentTooltip = (appointment, status, jsEvent) => {
    if (!appointment) return;

    clearTooltipHideTimeout();
    setHoveredEvent(buildHoveredEventPayload(appointment, status));
    setTooltipPosition({
      x: jsEvent?.clientX ?? tooltipPosition.x,
      y: jsEvent?.clientY ?? tooltipPosition.y,
    });
  };

  const scheduleTooltipHide = () => {
    clearTooltipHideTimeout();
    tooltipHideTimeoutRef.current = setTimeout(() => {
      setHoveredEvent(null);
    }, 200);
  };

  useEffect(
    () => () => {
      clearTooltipHideTimeout();
    },
    []
  );

  const user = useSelector((state) => state?.user);

  const isCareManager =
    user?.user?.role === "careManager" ||
    user?.user?.providerType === "Care Manager";

  const isTherapistProvider =
    user?.user?.role === "provider" &&
    user?.user?.providerType !== "Care Manager";

  const useModernScheduleUI = isCareManager || isTherapistProvider;

  const CANCELLED_APPOINTMENT_STATUSES = [
    "Cancelled",
    "Canceled",
    "canceled_by_user",
    "Canceled by Provider",
    "Canceled by Super Admin",
    "System Cancel",
    "late_cancelled",
    "late_cancel",
  ];

  const isAppointmentCancelled = (appointment) =>
    appointment?.isCancelled === true ||
    CANCELLED_APPOINTMENT_STATUSES.includes(appointment?.status);

  const getAppointmentSortTime = (appointment) => {
    if (appointment?.createdAt) {
      return new Date(appointment.createdAt).getTime();
    }

    const id = appointment?._id;
    if (typeof id === "string" && id.length === 24) {
      return parseInt(id.substring(0, 8), 16) * 1000;
    }

    if (appointment?.appointmenttime) {
      return new Date(appointment.appointmenttime).getTime();
    }

    return 0;
  };

  const parseEventDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getAppointmentCalendarTimes = (appointment) => {
    const start = moment(appointment?.appointmenttime);
    if (!start.isValid()) return null;

    const sessionMinutes = Math.max(
      15,
      Number(appointment.sessionLength) || Number(appointment.duration) || 15,
    );

    if (isTherapistProvider) {
      return {
        endTime: start.clone().add(sessionMinutes, "minutes").toDate(),
        startTime: start.toDate(),
      };
    }

    let end = appointment.appointmentEndTime
      ? moment(appointment.appointmentEndTime)
      : start.clone().add(sessionMinutes, "minutes");

    if (!end.isValid() || !end.isAfter(start)) {
      end = start.clone().add(sessionMinutes, "minutes");
    }

    const durationMinutes = end.diff(start, "minutes");
    if (durationMinutes > Math.max(sessionMinutes * 3, 180)) {
      end = start.clone().add(sessionMinutes, "minutes");
    }

    return {
      endTime: end.toDate(),
      startTime: start.toDate(),
    };
  };

  const businessId =
    user.user?.businessId ?? localStorage.getItem("businessId");
  const businessIdentifier =
    user.user?.businessIdentifier ??
    localStorage.getItem("businessIdentifier") ??
    "false";

  const handleNext = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.next();
      updateHeaderTitle(calendarApi);
    }
  };

  const handlePrevious = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.prev();
      updateHeaderTitle(calendarApi);
    }
  };

  const handleToday = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
      updateHeaderTitle(calendarApi);
    }
  };

  const updateHeaderTitle = (calendarApi) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const shortMonthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const viewType = calendarApi.view.type;
    const date = calendarApi.getDate();

    if (viewType === "timeGridDay") {
      setHeaderTitle(`${monthNames[date.getMonth()]} ${date.getDate()}`);
      return;
    }

    if (viewType === "timeGridWeek" && useModernScheduleUI) {
      const weekStart = new Date(calendarApi.view.currentStart);
      const weekEnd = new Date(calendarApi.view.currentEnd);
      weekEnd.setDate(weekEnd.getDate() - 1);
      setHeaderTitle(
        `${shortMonthNames[weekStart.getMonth()]} ${weekStart.getDate()} - ${shortMonthNames[weekEnd.getMonth()]} ${weekEnd.getDate()}`,
      );
      return;
    }

    setHeaderTitle(`${monthNames[date.getMonth()]} ${date.getFullYear()}`);
  };

  const injectCornerClockIcon = () => {
    if (!useModernScheduleUI || !calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    const viewType = calendarApi.view?.type;

    if (viewType !== "timeGridWeek" && viewType !== "timeGridDay") return;

    const axisCell =
      calendarApi.el?.querySelector(".fc-col-header .fc-timegrid-axis") ||
      calendarApi.el?.querySelector(
        ".fc-scrollgrid-section-header .fc-timegrid-axis"
      );

    if (!axisCell) return;

    const cornerFrame = axisCell.querySelector(".fc-timegrid-axis-frame");
    if (!cornerFrame) return;

    cornerFrame.innerHTML =
      '<span class="cm-corner-clock" aria-hidden="true"></span>';
  };

  const syncTimeGridAxisColumn = (calendarEl) => {
    const isWeek = calendarEl?.closest(".view-timeGridWeek");
    const isDay = calendarEl?.closest(".view-timeGridDay");
    if (!isWeek && !isDay) return;

    const px = "88px";

    calendarEl.querySelectorAll("colgroup col").forEach((col, index) => {
      if (index === 0) {
        col.style.width = px;
        col.style.minWidth = px;
        col.style.maxWidth = px;
      }
    });

    calendarEl
      .querySelectorAll(
        ".fc-timegrid-slots colgroup col:first-child, .fc-timegrid-cols colgroup col:first-child, .fc-col-header colgroup col:first-child",
      )
      .forEach((col) => {
        col.style.width = px;
        col.style.minWidth = px;
        col.style.maxWidth = px;
      });

    calendarEl
      .querySelectorAll(
        [
          "th.fc-timegrid-axis",
          "td.fc-timegrid-axis",
          "td.fc-timegrid-slot-label",
          ".fc-scrollgrid-shrink",
        ].join(", "),
      )
      .forEach((el) => {
        el.style.width = px;
        el.style.minWidth = px;
        el.style.maxWidth = px;
        el.style.boxSizing = "border-box";
      });

    calendarEl
      .querySelectorAll(
        [
          "td.fc-timegrid-slot-label .fc-timegrid-slot-label-frame",
          "th.fc-timegrid-axis .fc-timegrid-axis-frame",
          ".fc-scrollgrid-shrink-frame",
        ].join(", "),
      )
      .forEach((el) => {
        el.style.width = "100%";
        el.style.maxWidth = px;
        el.style.boxSizing = "border-box";
      });

    calendarEl
      .querySelectorAll(".fc-timegrid-cols td.fc-timegrid-axis")
      .forEach((el) => {
        el.style.width = px;
        el.style.minWidth = px;
        el.style.maxWidth = px;
      });

    calendarEl.querySelectorAll("td.fc-timegrid-slot-label").forEach((el) => {
      el.style.setProperty("border-right", "1px solid #e8e8ed", "important");
      el.style.setProperty("background", "#fff", "important");
      el.style.setProperty("text-align", "center", "important");
    });

    calendarEl
      .querySelectorAll(
        "td.fc-timegrid-slot-label .fc-timegrid-slot-label-frame",
      )
      .forEach((el) => {
        el.style.setProperty("text-align", "center", "important");
      });
  };

  const syncTimeGridLayout = (calendarApi) => {
    if (!calendarApi) return;

    const viewType = calendarApi.view?.type;

    if (viewType === "timeGridWeek" || viewType === "timeGridDay") {
      const run = () => {
        calendarApi.updateSize();
        syncTimeGridAxisColumn(calendarApi.el);
        injectCornerClockIcon();
      };

      requestAnimationFrame(run);
      requestAnimationFrame(() => requestAnimationFrame(run));
      setTimeout(run, 50);
      setTimeout(run, 200);
    }
  };

  const handleDatesSet = () => {
    updateVisibleDateRange();
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      updateHeaderTitle(calendarApi);
      syncTimeGridLayout(calendarApi);
    }
  };

  const handleViewDidMount = () => {
    if (!calendarRef.current) return;
    syncTimeGridLayout(calendarRef.current.getApi());
  };

  const updateVisibleDateRange = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;
      setVisibleDateRange({
        end: view.activeEnd,
        start: view.activeStart,
      });
    }
  };

  const hasEventsInVisibleRange = () => {
    if (!visibleDateRange.start || !visibleDateRange.end || !events.length) {
      return false;
    }

    return events.some((event) => {
      const eventEnd = new Date(event.end);
      const eventStart = new Date(event.start);

      return (
        eventStart < visibleDateRange.end && eventEnd > visibleDateRange.start
      );
    });
  };

  const handleViewChange = (view) => {
    setCurrentView(view);

    if (useModernScheduleUI) return;

    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(view);
      updateHeaderTitle(calendarApi);
    }
  };

  const isWithinJoinWindow = (appointment) => {
    if (!appointment?.appointmenttime) return false;

    const now = new Date();
    const appointmentStart = new Date(appointment.appointmenttime);
    const timeDiff = appointmentStart.getTime() - now.getTime();
    const minutesDiff = timeDiff / (1000 * 60);

    return minutesDiff <= 10 && minutesDiff >= -appointment.sessionLength;
  };

  const fetchAppointments = async () => {
    const authToken = user.user?.token;
    const providerId = user?.user?.providerId;

    if (!authToken) return;

    if (isCareManager) {
      if (!providerId) return;
    } else if (
      businessId === null ||
      businessId === undefined ||
      businessIdentifier === null ||
      businessIdentifier === undefined
    ) {
      return;
    }

    try {
      const role = user?.user?.role || "provider";
      const url = `${process.env.REACT_APP_BASE_URL}/appointment/getall?businessId=${businessId ?? ""}&businessIdentifier=${businessIdentifier}&role=${role}&providerId=${providerId}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!response.data.data || !Array.isArray(response.data.data))
        return toast.error("Invalid appointment data received");

      const appointments = response.data.data
        .map((appointment, index) => {
          try {
            const calendarTimes = getAppointmentCalendarTimes(appointment);
            if (!calendarTimes) return null;

            const { endTime, startTime } = calendarTimes;

            const provider =
              appointment.provider || appointment.health_specialist;

            let newStatus;
            const status = appointment.status;
            const isCancelled = isAppointmentCancelled(appointment);

            if (isCareManager) {
              newStatus = mapAppointmentToCmDisplayStatus(appointment);
            } else if (isCancelled) {
              // Cancel types mapping
              if (status === "canceled_by_user") {
                newStatus = "User Cancel";
              }
              else if (status === "Canceled by Provider") {
                newStatus = "Provider Cancel";
              }
              else if (status === "Canceled by Super Admin" || status === "System Cancel") {
                newStatus = "Admin Cancel";
              }
              else if (status === "late_cancelled" || status === "late_cancel") {
                newStatus = "Late Cancel";
              }
              else {
                newStatus = "Cancelled";
              }
            }
            else if (status === "Completed") {
              newStatus = "Completed";
            }
            else if (status === "No Show") {
              newStatus = "No Show";
            }
            else if (status === "Upcoming") {
              const now = new Date();

              if (endTime < now) {
                newStatus = "Completed";
              } else {
                newStatus = "Scheduled";
              }
            }
            else {
              newStatus = "Default";
            }

            const eventColor = isTherapistProvider
              ? getTherapistEventColor(newStatus, isCancelled)
              : getEventColor(
                  newStatus,
                  appointment.isCancelled,
                  isCareManager,
                );

            const mappedAppointment = {
              backgroundColor: eventColor,
              borderColor: eventColor,
              end: endTime,
              extendedProps: {
                appointment: appointment,
                createdAt: getAppointmentSortTime(appointment),
                endTime: endTime,
                provider: provider,
                startTime: startTime,
                status: newStatus,
                user: appointment.user,
              },
              id: appointment._id,
              title: `${appointment.user?.firstname ?? "Unknown"} ${appointment.user?.lastname ?? ""
                }`,
              start: startTime,
            };

            return mappedAppointment;
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean);

      setEvents(appointments);
    } catch (error) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch appointments."
      );
    }
  };

  const getEventColor = (status, isCancelled, forCareManager = isCareManager) => {
    if (forCareManager) {
      return getCmStatusColors(status).bg;
    }

    if (isCancelled) return "#FFD700";

    switch (status?.toLowerCase()) {
      case "cancelled":
        return "#FFD700";
      case "completed":
        return "#27B99A";
      case "no-show":
        return "#6C5CE7";
      case "scheduled":
        return "#1976d2";
      case "upcoming":
        return "#1976d2";
      default:
        return "#1976d2";
    }
  };

  const getTherapistEventColor = (status, isCancelled) => {
    if (isCancelled) return "#fef9c7";

    switch (status?.toLowerCase()) {
      case "completed":
        return "#d0fae5";
      case "no show":
        return "#fee4e6";
      case "scheduled":
      case "upcoming":
      case "default":
        return "#e3f2fd";
      default:
        return "#e3f2fd";
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    if (!useModernScheduleUI || !isCareManager) return;
    setCurrentView((view) => (view === "timeGridWeek" ? view : "timeGridWeek"));
  }, [isCareManager, useModernScheduleUI]);

  useEffect(() => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.setOption("slotEventOverlap", false);
    calendarApi.setOption("eventMaxStack", undefined);
    calendarApi.setOption("slotMinTime", "00:00:00");
    calendarApi.setOption(
      "slotMaxTime",
      isCareManager ? "24:00:00" : "23:59:59",
    );
  }, [currentView, isCareManager]);

  useEffect(() => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    updateHeaderTitle(calendarApi);
    updateVisibleDateRange();
    syncTimeGridLayout(calendarApi);
  }, [events, currentView, isCareManager]);

  useEffect(() => {
    if (!calendarRef.current || currentView !== "timeGridWeek" || !events.length) {
      return;
    }

    const calendarApi = calendarRef.current.getApi();
    const viewStart = calendarApi.view?.currentStart;
    const viewEnd = calendarApi.view?.currentEnd;

    if (!viewStart || !viewEnd) return;

    const firstVisibleEvent = events
      .map((event) => ({ start: parseEventDate(event.start) }))
      .filter(
        (event) =>
          event.start &&
          event.start >= viewStart &&
          event.start < viewEnd,
      )
      .sort((left, right) => left.start - right.start)[0];

    if (!firstVisibleEvent?.start) return;

    const hours = String(firstVisibleEvent.start.getHours()).padStart(2, "0");
    const minutes = String(firstVisibleEvent.start.getMinutes()).padStart(2, "0");

    calendarApi.scrollToTime(`${hours}:${minutes}:00`);
    syncTimeGridLayout(calendarApi);
  }, [events, currentView]);

  useEffect(() => {
    if (!user?.user?.token) return;

    if (isCareManager) {
      if (user?.user?.providerId) {
        fetchAppointments();
      }
      return;
    }

    if (
      businessId !== null &&
      businessId !== undefined &&
      businessIdentifier !== null &&
      businessIdentifier !== undefined
    ) {
      fetchAppointments();
    }
  }, [businessId, businessIdentifier, user?.user?.token, user?.user?.providerId, isCareManager]);

  const canAddAppointment = () =>
    isCareManager ||
    checkPermissionByKey(user, "schedule", "AddAppointment");

  const formatSlotTime = (date) => {
    const roundedMinutes = Math.round(date.getMinutes() / 15) * 15;
    const totalMinutes = date.getHours() * 60 + roundedMinutes;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const openScheduleDrawer = (date, time = null) => {
    if (!canAddAppointment()) return;

    setSelectedDate(new Date(date));
    setSelectedTime(time);
    setShowAddAppointment(true);
  };

  const handleDateClick = (arg) => {
    const clickedDate = new Date(arg.date);
    const isMonthView = currentView === "dayGridMonth";
    const isTimeGridView =
      currentView === "timeGridWeek" || currentView === "timeGridDay";
    const time =
      isTimeGridView && !arg.allDay ? formatSlotTime(clickedDate) : null;

    openScheduleDrawer(clickedDate, time);
  };

  const handleSelect = (selectInfo) => {
    if (calendarRef.current) {
      calendarRef.current.getApi().unselect();
    }

    const start = new Date(selectInfo.start);
    const isMonthView = currentView === "dayGridMonth";
    const time = isMonthView ? null : formatSlotTime(start);

    openScheduleDrawer(start, time);
  };

  const handleEventClick = (clickInfo) => {
    const appointment = clickInfo.event.extendedProps.appointment;
    const status = clickInfo.event.extendedProps.status;

    if (isCareManager) {
      setHoveredEvent(null);
      setDetailsAppointment(appointment);
      setDetailsDisplayStatus(status);
      setShowCheckInDetailsDrawer(true);
      return;
    }

    setSelectedAppointment(appointment);

    if (isWithinJoinWindow(appointment)) {
      setShowJoinModal(true);
    } else {
      setShowEditAppointment(true);
    }
  };

  const handleEventMouseEnter = (mouseEnterInfo) => {
    if (isCareManager) return;

    const appointment = mouseEnterInfo.event.extendedProps.appointment;
    const status = mouseEnterInfo.event.extendedProps.status;

    showAppointmentTooltip(appointment, status, mouseEnterInfo.jsEvent);
  };

  const handleEventMouseLeave = () => {
    scheduleTooltipHide();
  };

  const formatWeekSessionTime = (date) =>
    date
      .toLocaleTimeString([], {
        hour: "2-digit",
        hour12: true,
        minute: "2-digit",
      })
      .replace(/\s(am|pm)/i, (match) => match.toUpperCase());

  const formatTherapistWeekSlotLabel = (date) => {
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    if (hours === 0) {
      return `12:${minutes}`;
    }

    if (hours < 10) {
      return `0${hours}:${minutes}`;
    }

    if (hours <= 12) {
      return `${hours}:${minutes}`;
    }

    return `${hours - 12}:${minutes}`;
  };

  const renderModernEventContent = ({
    clientName,
    startTimeStr,
    useCancelledStyle,
    dayTimeLabel,
  }) => {
    const displayName =
      currentView === "timeGridDay"
        ? clientName
        : clientName.split(" ")[0] || clientName;
    const compactName = displayName;
    const eventTimeLabel =
      currentView === "timeGridDay"
        ? dayTimeLabel || ""
        : currentView === "dayGridMonth" || currentView === "timeGridWeek"
          ? String(startTimeStr || "").replace(/\s(?=am|pm)/i, "")
          : startTimeStr;

    return (
      <div
        className={`cm-event-content${
          currentView === "timeGridWeek" ? " cm-week-event-content" : ""
        }${currentView === "timeGridDay" ? " cm-day-event-content" : ""}${
          currentView === "dayGridMonth" ? " cm-month-event-content" : ""
        }`}
      >
        <span className={`cm-event-name ${useCancelledStyle ? "cancelled" : ""}`}>
          {compactName}
        </span>
        {eventTimeLabel && (
          <span className={`cm-event-time ${useCancelledStyle ? "cancelled" : ""}`}>
            {eventTimeLabel}
          </span>
        )}
      </div>
    );
  };

  const renderEventContent = (eventInfo) => {
    const { appointment, endTime, startTime, status } =
      eventInfo.event.extendedProps;

    const clientName = eventInfo.event.title;
    const isCancelled = isAppointmentCancelled(appointment);
    const useCancelledStyle = isCancelled && !isCareManager;

    const parsedStart = parseEventDate(startTime);
    const parsedEnd = parseEventDate(endTime);

    if (parsedStart == null || parsedEnd == null) {
      return (
        <div className="fc-event-content">
          <div className={`fc-event-title ${isCancelled ? "cancelled" : ""}`}>
            {clientName}
          </div>
        </div>
      );
    }

    let endTimeStr, startTimeStr;

    try {
      startTimeStr = parsedStart
        .toLocaleTimeString([], {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        })
        .toLowerCase();

      endTimeStr = parsedEnd
        .toLocaleTimeString([], {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        })
        .toLowerCase();
    } catch (error) {
      endTimeStr = "Time unavailable";
      startTimeStr = "Time unavailable";
    }

    if (isCareManager && currentView === "timeGridWeek") {
      const timeRange = `${formatWeekSessionTime(parsedStart)} — ${formatWeekSessionTime(parsedEnd)}`;

      return (
        <div className="fc-event-content cm-week-session-content">
          <div className={`fc-event-title ${useCancelledStyle ? "cancelled" : ""}`}>
            {clientName}
          </div>
          <div className={`fc-event-time ${useCancelledStyle ? "cancelled" : ""}`}>
            {timeRange}
          </div>
          {status && (
            <div className={`fc-event-status ${useCancelledStyle ? "cancelled" : ""}`}>
              {status}
            </div>
          )}
        </div>
      );
    }

    if (useModernScheduleUI) {
      return renderModernEventContent({
        clientName,
        dayTimeLabel:
          currentView === "timeGridDay"
            ? formatTherapistWeekSlotLabel(parsedStart)
            : undefined,
        startTimeStr,
        useCancelledStyle,
      });
    }

    return (
      <div className="fc-event-content">
        <div className={`fc-event-title ${isCancelled ? "cancelled" : ""}`}>
          {clientName}
        </div>

        <div className={`fc-event-time ${isCancelled ? "cancelled" : ""}`}>
          {startTimeStr} - {endTimeStr}
        </div>

        {status && (
          <div className={`fc-event-status ${isCancelled ? "cancelled" : ""}`}>
            {status}
          </div>
        )}
      </div>
    );
  };

  const handleMoreEventsClick = (arg) => {
    try {
      const segList =
        arg?.hiddenSegs?.length > 0
          ? arg.hiddenSegs
          : Array.isArray(arg?.allSegs)
            ? arg.allSegs
            : null;

      if (!segList?.length) return true;

      const appointmentEvents = segList
        .map((seg) => {
          const fcEvent = seg?.event;
          if (!fcEvent) return null;

          return {
            title: fcEvent.title,
            extendedProps: { ...fcEvent.extendedProps },
          };
        })
        .filter(Boolean);

      if (appointmentEvents.length > 0) {
        setMultipleAppointments(appointmentEvents);
        setShowMultipleAppointments(true);
      }

      return true;
    } catch (error) {
      toast.error("Unable to load multiple appointments");
      return true;
    }
  };

  const renderTimeGridMoreLink = (arg) => arg.shortText;

  const renderMoreLinkContent = (arg) => {
    if (isTherapistProvider && currentView === "dayGridMonth") {
      return `${arg.num}+`;
    }
    return renderTimeGridMoreLink(arg);
  };

  const handleAddAppointmentFromDate = (date) => {
    openScheduleDrawer(date, null);
  };

  const handleJoinCall = async () => {
    // Always fetch: tokens cached on the appointment can be expired.
    let credentials;
    setIsLoadingAgoraToken(true);

    try {
      const authToken = user.user?.token;
      const result = await fetchAgoraToken(selectedAppointment._id, authToken);

      if (result.success) {
        credentials = result.data;
      } else {
        toast.error(`Failed to setup video call: ${result.error}`);
        return;
      }
    } catch (error) {
      console.log("-> -> handleJoinCall -> error->", error);
      toast.error("Failed to setup video call. Please try again.");
      return;
    } finally {
      setIsLoadingAgoraToken(false);
    }

    if (
      !openVideoCallTab({
        agoraCredentials: credentials,
        appointment: selectedAppointment,
        isCareManager:
          user.user?.role === "careManager" ||
          user.user?.providerType === "Care Manager",
        provider: user.user,
        user: selectedAppointment.user,
      })
    ) {
      toast.error("Unable to open video call in new tab.");
    }
  };

  const handleCancelAppointmentClick = (appointment) => {
    setAppointmentToCancel(appointment);
    setCancelAppointmentScope("single");
    setCancellationReason("");
    setCustomMessage("");
    setHoveredEvent(null);
    setShowCancelConfirmation(true);
  };

  const cancellationReasons = [
    "Emergency situation",
    "Health reasons",
    "Patient requested cancellation",
    "Provider unavailable",
    "Scheduling conflict",
    "Technical issues",
    "Weather conditions",
    "Other",
  ];

  const handleCancelAppointment = async () => {
    try {
      if (!appointmentToCancel?._id) {
        toast.error("Invalid appointment data");
        return;
      }

      if (!cancellationReason) {
        toast.error("Please select a cancellation reason");
        return;
      }

      if (appointmentToCancel?.isCancelled) {
        toast.error("Appointment is already cancelled.");
        return;
      }

      setIsCancellingAppointment(true);

      const authToken = user.user?.token;

      const body = {
        cancelAllFuture: cancelAppointmentScope === "future",
        cancellationReason,
        customMessage: customMessage.trim() ? customMessage.trim() : undefined,
        timeZone: moment.tz.guess(),
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/cancel/${appointmentToCancel._id}`,
        body,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        fetchAppointments();
        setAppointmentToCancel(null);
        setCancelAppointmentScope("single");
        setCancellationReason("");
        setCustomMessage("");
        setShowCancelConfirmation(false);
        toast.success("Appointment cancelled successfully");
      } else {
        toast.error(response.data.message ?? "Failed to cancel appointment");
      }
    } catch (error) {
      console.error("-> -> handleCancelAppointment -> error->", error);
      toast.error(
        error.response?.data?.message ?? "Failed to cancel appointment"
      );
    } finally {
      setIsCancellingAppointment(false);
    }
  };

  const handleRescheduleAppointment = () => {
    setShowJoinModal(false);
    setShowRescheduleModal(true);
  };

  const handleTooltipJoin = async (appointment) => {
    try {
      setSelectedAppointment(appointment);
      setHoveredEvent(null);

      // Always fetch: tokens cached on the appointment can be expired.
      let credentials;
      setIsLoadingAgoraToken(true);

      try {
        const authToken = user.user?.token;
        const result = await fetchAgoraToken(appointment._id, authToken);

        if (result.success) {
          credentials = result.data;
        } else {
          toast.error(`Failed to setup video call: ${result.error}`);
          return;
        }
      } catch (error) {
        console.error("-> -> handleTooltipJoin -> error->", error);
        toast.error("Failed to setup video call. Please try again.");
        return;
      } finally {
        setIsLoadingAgoraToken(false);
      }

      if (
        !openVideoCallTab({
          agoraCredentials: credentials,
          appointment,
          isCareManager:
            user.user?.role === "careManager" ||
            user.user?.providerType === "Care Manager",
          provider: user.user,
          user: appointment.user,
        })
      ) {
        toast.error("Unable to open video call in new tab.");
      }
    } catch (error) {
      console.error("-> -> handleTooltipJoin -> error->", error);
      setHoveredEvent(null);
    }
  };

  const handleTooltipCancel = async (appointment) => {
    try {
      setAppointmentToCancel(appointment);
      setCancellationReason("");
      setCustomMessage("");
      setHoveredEvent(null);
      setShowCancelConfirmation(true);
    } catch (error) {
      console.error("-> -> handleTooltipCancel -> error->", error);
      setHoveredEvent(null);
    }
  };

  const handleTooltipReschedule = (appointment) => {
    try {
      setHoveredEvent(null);
      setSelectedAppointment(appointment);
      setShowRescheduleModal(true);
    } catch (error) {
      console.error("-> -> handleTooltipReschedule -> error->", error);
      setHoveredEvent(null);
    }
  };

  const handleNoSessions =
    !hideNoSessionMessage &&
    !hasEventsInVisibleRange() &&
    visibleDateRange.start &&
    visibleDateRange.end &&
    (currentView === "timeGridDay" || currentView === "timeGridWeek");

  return (
    <div
      className={`app-page ${
        isTherapistProvider ? "therapist-schedule-page" : ""
      }`}
    >
      <div
        className={`appointment-calendar-container care-manager-schedule ${
          isTherapistProvider ? "therapist-schedule" : ""
        } provider-schedule-inner view-${currentView}${
          !isTherapistProvider ? " bg-white rounded shadow-sm provider-page-card" : ""
        }`}
      >
          <div className="calendar-main">
        <div className="calendar-header cm-calendar-header">
          <div className="calendar-title">
            <div className="calendar-navigation">
              <h2 className="current-month">{headerTitle}</h2>

              <button
                aria-label="Previous"
                className="nav-btn"
                onClick={handlePrevious}>
                ‹
              </button>

              <button aria-label="Next" className="nav-btn" onClick={handleNext}>
                ›
              </button>

              {!useModernScheduleUI && (
                <button className="nav-btn today-btn" onClick={handleToday}>
                  Today
                </button>
              )}
            </div>
          </div>

          <div className="calendar-controls">
            {canAddAppointment() && (
              <button
                aria-label="Add appointment"
                className="add-btn cm-add-btn"
                onClick={() => openScheduleDrawer(new Date(), null)}>
                +
              </button>
            )}

            <div className="view-toggle-group cm-view-toggle">
              <button
                className={`view-btn ${currentView === "timeGridDay" ? "active" : ""}`}
                onClick={() => handleViewChange("timeGridDay")}>
                DAY
              </button>

              <button
                className={`view-btn ${currentView === "timeGridWeek" ? "active" : ""}`}
                onClick={() => handleViewChange("timeGridWeek")}>
                WEEK
              </button>

              <button
                className={`view-btn ${currentView === "dayGridMonth" ? "active" : ""}`}
                onClick={() => handleViewChange("dayGridMonth")}>
                MONTH
              </button>
            </div>
          </div>
        </div>

        <FullCalendar
          key={useModernScheduleUI ? currentView : "legacy-calendar"}
          allDaySlot={false}
          businessHours={false}
          dateClick={handleDateClick}
          datesSet={handleDatesSet}
          dayHeaderContent={(arg) => {
            const dayName = arg.date
              .toLocaleDateString("en-US", { weekday: "long" })
              .toUpperCase();
            const dayNum = arg.date.getDate();
            const isToday =
              arg.date.toDateString() === new Date().toDateString();

            if (currentView === "dayGridMonth") {
              return { html: `<span>${dayName}</span>` };
            }

            if (currentView === "timeGridDay") {
              return {
                html: `<span class="cm-day-only-header">${dayName}</span>`,
              };
            }

            return {
              html: `<div class="cm-day-header"><span class="cm-day-name">${dayName}</span><span class="cm-day-num${isToday ? " is-today" : ""}">${dayNum}${isToday ? '<span class="cm-today-dot"></span>' : ""}</span></div>`,
            };
          }}
          dayMaxEventRows={false}
          dayMaxEvents={1}
          editable={true}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          eventMinHeight={24}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          eventOrder="-extendedProps.createdAt,start"
          expandRows={currentView === "dayGridMonth" ? undefined : false}
          events={
            events && events.length > 0
              ? events.map((event) => {
                const appointment = event.extendedProps?.appointment;
                const statusSlug = isCareManager
                  ? getCmStatusSlug(event.extendedProps?.status)
                  : (
                    isAppointmentCancelled(appointment)
                      ? "cancelled"
                      : event.extendedProps?.status || "default"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-");

                return {
                  ...event,
                  className: `status-${statusSlug}`,
                };
              })
              : []
          }
          headerToolbar={false}
          height={
            currentView === "dayGridMonth"
              ? "auto"
              : "calc(100vh - 140px)"
          }
          initialView={currentView}
          moreLinkClass="more-link"
          moreLinkClick={handleMoreEventsClick}
          moreLinkContent={renderMoreLinkContent}
          moreLinkText=""
          nowIndicator={true}
          plugins={[
            dayGridPlugin,
            interactionPlugin,
            listPlugin,
            timeGridPlugin,
          ]}
          ref={calendarRef}
          scrollTime="09:00:00"
          select={handleSelect}
          selectable={true}
          selectMirror={!useModernScheduleUI}
          slotDuration="00:30:00"
          slotEventOverlap={false}
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            meridiem: "short",
          }}
          slotLabelContent={
            isTherapistProvider &&
            (currentView === "timeGridWeek" || currentView === "timeGridDay")
              ? (arg) => ({
                  html: `<span class="cm-week-slot-label">${formatTherapistWeekSlotLabel(arg.date)}</span>`,
                })
              : undefined
          }
          slotLabelClassNames={
            isTherapistProvider &&
            (currentView === "timeGridWeek" || currentView === "timeGridDay")
              ? "cm-week-slot-label"
              : undefined
          }
          slotLabelInterval="00:30:00"
          slotLaneClassNames={
            currentView === "timeGridWeek" || currentView === "timeGridDay"
              ? "cm-week-slot-lane"
              : "custom-slot-lane"
          }
          slotMaxTime="24:00:00"
          slotMinTime="00:00:00"
          timeZone="local"
          viewDidMount={handleViewDidMount}
          weekends={true}
        />

        {handleNoSessions && !hideNoSessionMessage && (
          <div className="no-sessions-message">
            <div
              className="no-sessions-content"
              style={{ position: "relative" }}>
              <button
                aria-label="Close"
                className="no-sessions-close-btn"
                onClick={() => setHideNoSessionMessage(true)}>
                &times;
              </button>

              <div className="no-sessions-icon">📅</div>

              <div className="no-sessions-text">
                {currentView === "timeGridDay"
                  ? "No sessions scheduled for this day."
                  : "No sessions scheduled for this week."}
              </div>
            </div>
          </div>
        )}

        {!useModernScheduleUI && (
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-dot existing-client"></div>
            <span>Existing Client</span>
          </div>

          <div className="legend-item">
            <div className="legend-dot new-client"></div>
            <span>New Client</span>
          </div>

          <div className="legend-item">
            <div className="legend-dot cancelled-client"></div>
            <span>Cancelled</span>
          </div>
        </div>
        )}
      </div>

      {hoveredEvent && (
        <div
          className="appointment-tooltip"
          onMouseEnter={clearTooltipHideTimeout}
          onMouseLeave={() => {
            setHoveredEvent(null);
          }}
          style={{
            left: Math.min(tooltipPosition.x + 10, window.innerWidth - 350),
            position: "fixed",
            top: Math.max(tooltipPosition.y - 10, 10),
            zIndex: 1000,
          }}>
          <div className="tooltip-content">
            <div className="tooltip-left">
              <div className="tooltip-header">
                <div className="client-name">
                  {hoveredEvent.user?.firstname} {hoveredEvent.user?.lastname}
                </div>
              </div>

              <div className="tooltip-time">
                <span className="time-icon">🕐</span>

                {new Date(hoveredEvent.appointmenttime).toLocaleTimeString([], {
                  hour: "2-digit",
                  hour12: true,
                  minute: "2-digit",
                })}
                {hoveredEvent.appointmentEndTime &&
                  " - " +
                  new Date(
                    hoveredEvent.appointmentEndTime
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    hour12: true,
                    minute: "2-digit",
                  })}
              </div>

              {hoveredEvent.extendedProps.newStatus && (
                <div className="appointment-status">
                  {hoveredEvent.extendedProps.newStatus}
                </div>
              )}

              <div className="client-status">
                <span className="status-dot existing"></span>
                Existing Client
              </div>
            </div>

            {hoveredEvent?.extendedProps?.canRescheduleOrCancel && (
              <div className="tooltip-actions">
                {isWithinJoinWindow(hoveredEvent) &&
                  !hoveredEvent.isCancelled &&
                  hoveredEvent.status?.toLowerCase() !== "cancelled" && (
                    <button
                      className="action-btn join-btn"
                      disabled={isLoadingAgoraToken}
                      onClick={() => handleTooltipJoin(hoveredEvent)}
                      onMouseEnter={(e) => e.stopPropagation()}>
                      <span className="btn-icon">📹</span>

                      {isLoadingAgoraToken ? "Setting up..." : "Join"}
                    </button>
                  )}

                <button
                  className="action-btn cancel-btn"
                  onClick={() => handleTooltipCancel(hoveredEvent)}
                  onMouseEnter={(e) => e.stopPropagation()}>
                  Cancel
                </button>

                <button
                  className="action-btn reschedule-btn"
                  onClick={() => handleTooltipReschedule(hoveredEvent)}
                  onMouseEnter={(e) => e.stopPropagation()}>
                  Reschedule
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAddAppointment && (
        <AddAppointment
          fetchAppointmentData={fetchAppointments}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          setShowAddAppointment={setShowAddAppointment}
        />
      )}

      {isCareManager && showCheckInDetailsDrawer && detailsAppointment && (
        <CheckInDetailsDrawer
          appointment={detailsAppointment}
          displayStatus={detailsDisplayStatus}
          fetchAppointmentData={fetchAppointments}
          onClose={() => {
            setShowCheckInDetailsDrawer(false);
            setDetailsAppointment(null);
            setDetailsDisplayStatus("");
          }}
        />
      )}

      {showRescheduleModal && selectedAppointment && (
        <div className="modal-overlay">
          <RescheduleAppointment
            appointmentData={selectedAppointment}
            fetchAppointmentData={fetchAppointments}
            setShowRescheduleModal={setShowRescheduleModal}
          />
        </div>
      )}

      {showJoinModal && selectedAppointment && (
        <div className="modal-overlay">
          <div className="join-appointment-modal">
            <div className="modal-header">
              <h3>Join Appointment</h3>

              <button
                className="close-btn"
                onClick={() => setShowJoinModal(false)}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="appointment-details">
                <div className="client-name">
                  {selectedAppointment.user?.firstname}{" "}
                  {selectedAppointment.user?.lastname}
                </div>

                <div className="appointment-time">
                  <span className="time-icon">🕐</span>

                  {new Date(
                    selectedAppointment.appointmenttime
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    hour12: true,
                    minute: "2-digit",
                  })}
                </div>

                <div className="client-status">
                  <span className="status-dot existing"></span>
                  Existing Client
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="action-btn join-btn"
                  disabled={isLoadingAgoraToken}
                  onClick={handleJoinCall}>
                  <span className="btn-icon">📹</span>

                  {isLoadingAgoraToken ? "Setting up..." : "Join Video Call"}
                </button>

                <button
                  className="action-btn cancel-btn"
                  onClick={() =>
                    handleCancelAppointmentClick(selectedAppointment)
                  }>
                  Cancel
                </button>

                <button
                  className="action-btn reschedule-btn"
                  onClick={handleRescheduleAppointment}>
                  Reschedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCancelConfirmation && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Cancellation</h3>

              {!isCancellingAppointment && (
                <button
                  className="close-btn"
                  onClick={() => {
                    setAppointmentToCancel(null);
                    setCancelAppointmentScope("single");
                    setCancellationReason("");
                    setCustomMessage("");
                    setShowCancelConfirmation(false);
                  }}>
                  &times;
                </button>
              )}
            </div>

            <div className="cancel-appointment-modal-body">
              <div className="confirmation-content">
                <div className="warning-icon">⚠️</div>

                <h4>Are you sure you want to cancel this appointment?</h4>

                <p>
                  This action will cancel the appointment for{" "}
                  <strong>
                    {appointmentToCancel?.user?.firstname}{" "}
                    {appointmentToCancel?.user?.lastname}
                  </strong>{" "}
                  scheduled for{" "}
                  <strong>
                    {new Date(
                      appointmentToCancel?.appointmenttime
                    ).toLocaleString()}
                  </strong>
                  .
                </p>

                <p>This action cannot be undone.</p>

                <div
                  className="form-group"
                  style={{ marginBottom: 16, textAlign: "left" }}>
                  <label className="form-label">Cancel Options</label>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}>
                    <label>
                      <input
                        checked={cancelAppointmentScope === "single"}
                        name="cancelAppointmentScope"
                        onChange={() => setCancelAppointmentScope("single")}
                        type="radio"
                        value="single"
                      />

                      <span className="ms-2">Cancel this appointment only</span>
                    </label>

                    <label>
                      <input
                        checked={cancelAppointmentScope === "future"}
                        name="cancelAppointmentScope"
                        onChange={() => setCancelAppointmentScope("future")}
                        type="radio"
                        value="future"
                      />

                      <span className="ms-2">
                        Cancel this and all future appointments
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="cancellationReason" className="form-label">
                    Cancellation Reason <span className="required">*</span>
                  </label>

                  <select
                    className="form-select"
                    id="cancellationReason"
                    onChange={(e) => setCancellationReason(e.target.value)}
                    required
                    value={cancellationReason}>
                    <option value="">Select a reason</option>

                    {cancellationReasons.map((reason, index) => (
                      <option key={index} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="customMessage">
                    Message for client (Optional)
                  </label>

                  <textarea
                    className="form-textarea"
                    id="customMessage"
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Add a personal message to include in the cancellation notification..."
                    rows="3"
                    value={customMessage}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="action-btn cancel-btn"
                  disabled={!cancellationReason || isCancellingAppointment}
                  onClick={handleCancelAppointment}>
                  {isCancellingAppointment ? "Cancelling..." : "Confirm Cancel"}
                </button>

                <button
                  className="action-btn secondary-btn"
                  onClick={() => {
                    setAppointmentToCancel(null);
                    setCancelAppointmentScope("single");
                    setCancellationReason("");
                    setCustomMessage("");
                    setShowCancelConfirmation(false);
                  }}>
                  Keep Appointment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMultipleAppointments &&
        multipleAppointments &&
        multipleAppointments.length > 0 && (
          <div className="modal-overlay">
            <div className="multiple-appointments-modal">
              <div className="modal-header">
                <h3>Appointments</h3>

                <button
                  className="close-btn"
                  onClick={() => setShowMultipleAppointments(false)}>
                  &times;
                </button>
              </div>

              <div className="modal-body">
                {multipleAppointments.map((event, index) => {
                  const appointment = event.extendedProps?.appointment;
                  const status = event.extendedProps?.status;
                  const parsedStart = parseEventDate(
                    event.extendedProps?.startTime
                  );
                  const parsedEnd = parseEventDate(event.extendedProps?.endTime);

                  let timeDisplay = "Time unavailable";

                  if (parsedStart && parsedEnd) {
                    try {
                      const endStr = parsedEnd.toLocaleTimeString([], {
                        hour: "2-digit",
                        hour12: true,
                        minute: "2-digit",
                      });

                      const startStr = parsedStart.toLocaleTimeString([], {
                        hour: "2-digit",
                        hour12: true,
                        minute: "2-digit",
                      });

                      timeDisplay = `${startStr} - ${endStr}`;
                    } catch (error) {
                      console.error("-> -> error->", error);
                    }
                  }

                  const patientName =
                    event.title ||
                    `${appointment?.user?.firstname ?? ""} ${
                      appointment?.user?.lastname ?? ""
                    }`.trim() ||
                    "Unknown";

                  return (
                    <div
                      className="appointment-item"
                      key={event.extendedProps?.appointment?._id || index}
                      onClick={() => {
                        if (!appointment) return;

                        setShowMultipleAppointments(false);

                        if (isCareManager) {
                          setDetailsAppointment(appointment);
                          setDetailsDisplayStatus(status);
                          setShowCheckInDetailsDrawer(true);
                          return;
                        }

                        setSelectedAppointment(appointment);

                        if (isWithinJoinWindow(appointment)) {
                          setShowJoinModal(true);
                        } else {
                          setShowEditAppointment(true);
                        }
                      }}
                      style={{ cursor: "pointer" }}>
                      <div className="appointment-time">{timeDisplay}</div>

                      <div className="appointment-details">
                        <div className="appointment-title">{patientName}</div>

                        <div className="appointment-status">
                          {status || "Unknown"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCalendar;
